import { NavLink, useLocation } from "react-router-dom";
import { Search, PanelLeftClose, PanelLeftOpen, Plus, X, House, Shapes, Layers, Users, Lock } from "lucide-react";
import { BrynLogo, BrynMark } from "@/components/BrynLogo";
import { AccessIndicator } from "@/components/Access";
import { SidebarRecents, rowCls } from "@/components/SidebarRecents";
import { ProfileMenu } from "@/components/ProfileMenu";

const NAV = [
  { to: "/app", end: true, icon: House, label: "Home", testid: "nav-home" },
  { to: "/app/artifacts", icon: Shapes, label: "Artifacts", testid: "nav-artifacts" },
  { to: "/app/flashcards", icon: Layers, label: "Flashcards", testid: "nav-flashcards", feature: "practice" },
  { to: "/app/groups", icon: Users, label: "Groups", testid: "nav-groups" },
];
const ICON = "h-[18px] w-[18px] shrink-0 stroke-[1.6]";

const isOn = (item, path) => (item.end ? path === item.to || path === `${item.to}/` : path.startsWith(item.to));

function NavItem({ item, path, ent, rail, onPick }) {
  const locked = item.feature && ent && !ent.features[item.feature];
  return (
    <NavLink to={item.to} end={item.end} onClick={onPick} data-testid={item.testid} title={rail ? item.label : undefined} aria-label={rail ? item.label : undefined}
      className={`${rowCls(isOn(item, path))} ${rail ? "justify-center px-0 w-10" : ""}`}>
      <item.icon className={ICON} />
      {!rail && <span className="flex-1 truncate">{item.label}</span>}
      {!rail && locked && <Lock className="h-3 w-3 text-neutral-600" aria-label="Starter and above" data-testid={`${item.testid}-lock`} />}
    </NavLink>
  );
}

function IconBtn({ label, onClick, testid, children }) {
  return (
    <button onClick={onClick} aria-label={label} title={label} data-testid={testid}
      className="h-9 w-9 rounded-lg flex items-center justify-center text-neutral-400 hover:text-white hover:bg-white/[0.06] outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 transition-colors">
      {children}
    </button>
  );
}

export function Rail({ user, ent, onExpand, onNew, onSearch, onLogout }) {
  const { pathname } = useLocation();
  return (
    <aside className="h-full w-[60px] flex flex-col items-center bg-[#0F131D] border-r border-white/[0.06] py-3 gap-1" data-testid="sidebar-rail">
      <BrynMark className="h-7 w-7 mb-2" />
      <IconBtn label="Show sidebar" onClick={onExpand} testid="sidebar-expand-btn"><PanelLeftOpen className={ICON} /></IconBtn>
      <IconBtn label="New" onClick={onNew} testid="rail-new-btn"><Plus className={ICON} /></IconBtn>
      <IconBtn label="Search chats" onClick={onSearch} testid="rail-search-btn"><Search className={ICON} /></IconBtn>
      <nav className="mt-3 flex flex-col gap-0.5" aria-label="Main">
        {NAV.map((i) => <NavItem key={i.to} item={i} path={pathname} ent={ent} rail />)}
      </nav>
      <div className="mt-auto"><ProfileMenu user={user} ent={ent} onLogout={onLogout} rail /></div>
    </aside>
  );
}

export function Sidebar({ user, ent, chats, onCollapse, onNew, onSearch, onDelete, onPick, onChanged, onLogout, mobile, onClose }) {
  const { pathname } = useLocation();
  return (
    <aside className={`h-full ${mobile ? "w-full" : "w-[264px] border-r border-white/[0.06]"} flex flex-col bg-[#0F131D]`} data-testid="sidebar">
      <div className="flex items-center justify-between pl-3 pr-2 h-14 shrink-0">
        <BrynLogo markClass="h-7 w-7" />
        <div className="flex items-center">
          <IconBtn label="Search chats" onClick={onSearch} testid="sidebar-search-btn"><Search className={ICON} /></IconBtn>
          {mobile ? <IconBtn label="Close menu" onClick={onClose} testid="sidebar-close-btn"><X className={ICON} /></IconBtn>
            : <IconBtn label="Hide sidebar" onClick={onCollapse} testid="sidebar-collapse-btn"><PanelLeftClose className={ICON} /></IconBtn>}
        </div>
      </div>
      <div className="px-2 shrink-0">
        <button onClick={onNew} data-testid="new-btn" className={`${rowCls(false)} w-full`}><Plus className={ICON} /> New</button>
        <nav className="mt-1 space-y-0.5" aria-label="Main">
          {NAV.map((i) => <NavItem key={i.to} item={i} path={pathname} ent={ent} onPick={onPick} />)}
        </nav>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto scroll-thin px-2 pb-3">
        <SidebarRecents chats={chats} onDelete={onDelete} onPick={onPick} onChanged={onChanged} />
      </div>
      <div className="shrink-0 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1">
        <AccessIndicator />
        <ProfileMenu user={user} ent={ent} onLogout={onLogout} onPick={onPick} />
      </div>
    </aside>
  );
}
